/**
 * The optional account session on this device.
 *
 * Sitting never requires this. The bearer lives in localStorage, wrapped because
 * private mode throws, and is sent as `Authorization`, never in a URL — native and
 * PWA do not share a cookie jar, so the protocol is one header shape.
 */

import { signal } from '@preact/signals';
import { apiOrigin } from '../config/origin.ts';

export const SESSION_KEY = 'brahm.session.v1';
export const PENDING_RECOVERY_KEY = 'brahm.recovery.pending.v1';

/** 32 random bytes, URL-safe base64 without padding. Matches the Worker bearer. */
const SESSION_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
/** Last seven days of a 30-day session; see worker/auth/model.ts. */
const SESSION_RENEW_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;

export interface AccountUser {
  id: string;
  username: string;
}

export interface AccountSession {
  sessionToken: string;
  expiresAt: number;
  user: AccountUser;
}

export interface PendingRecovery {
  code: string;
  rotated: boolean;
}

export class AuthError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
  }
}

export type JsonObject = Record<string, unknown>;

function storage(): Storage | null {
  try {
    const value: unknown = typeof localStorage === 'undefined' ? null : localStorage;
    if (!value || typeof (value as Storage).getItem !== 'function'
        || typeof (value as Storage).setItem !== 'function') return null;
    return value as Storage;
  } catch {
    return null;
  }
}

function readJson(key: string): unknown {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, JSON.stringify(value));
  } catch { /* private mode */ }
}

function isRecord(value: unknown): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function parseSession(value: unknown): AccountSession | null {
  if (!isRecord(value) || !isRecord(value.user)) return null;
  const token = value.sessionToken;
  const expiresAt = value.expiresAt;
  const id = value.user.id;
  const username = value.user.username;
  if (typeof token !== 'string' || !SESSION_TOKEN_RE.test(token)
      || typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)
      || typeof id !== 'string' || id.length === 0
      || typeof username !== 'string' || username.length === 0) return null;
  if (expiresAt <= Date.now()) return null;
  return { sessionToken: token, expiresAt, user: { id, username } };
}

function loadSession(): AccountSession | null {
  const parsed = parseSession(readJson(SESSION_KEY));
  if (!parsed) {
    writeJson(SESSION_KEY, null);
    return null;
  }
  return parsed;
}

function loadPending(): PendingRecovery | null {
  const value = readJson(PENDING_RECOVERY_KEY);
  if (!isRecord(value) || typeof value.code !== 'string' || value.code.length === 0
      || typeof value.rotated !== 'boolean') return null;
  return { code: value.code, rotated: value.rotated };
}

export const accountSession = signal<AccountSession | null>(loadSession());
export const pendingRecovery = signal<PendingRecovery | null>(loadPending());

export function storeSession(session: AccountSession | null): void {
  accountSession.value = session;
  writeJson(SESSION_KEY, session);
}

export function setPendingRecovery(value: PendingRecovery | null): void {
  pendingRecovery.value = value;
  writeJson(PENDING_RECOVERY_KEY, value);
}

function scheduleSync(): void {
  void import('./sync').then((mod) => { void mod.syncHistory(); }).catch(() => {
    /* Failure is a status on the Account screen, never a throw into a sit. */
  });
}

function sessionFrom(body: JsonObject): AccountSession {
  const session = parseSession({
    sessionToken: body.sessionToken,
    expiresAt: body.expiresAt,
    user: body.user,
  });
  if (!session) {
    throw new AuthError('invalid_json', 200,
      'The server sent a response this app could not read.');
  }
  return session;
}

function errorFromResponse(status: number, parsed: unknown): AuthError {
  const rec = isRecord(parsed) ? parsed : {};
  const code = typeof rec.error === 'string' ? rec.error : 'http_error';
  const serverMessage = typeof rec.message === 'string' ? rec.message : '';
  const message =
    code === 'invalid_session' ? 'Sign in again. This device is no longer signed in.'
    : code === 'invalid_recovery_code' ? 'That recovery code is not valid.'
    : code === 'invalid_pairing_code' ? 'That pairing code is not valid.'
    : code === 'rate_limited' ? 'Too many attempts. Wait before trying again.'
    : code === 'username_unavailable' ? 'Could not assign a name. Try creating the account again.'
    : serverMessage || 'The request could not be completed.';
  return new AuthError(code, status, message);
}

export async function authRequest(
  path: string,
  init: RequestInit = {},
): Promise<{ status: number; body: JsonObject | null }> {
  const headers = new Headers(init.headers);
  const session = accountSession.value;
  if (session && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${session.sessionToken}`);
  }
  if (init.body != null && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  let response: Response;
  try {
    // Absolute only inside the native shell. Its WebView is served from https://localhost,
    // so a relative path would ask the APK's own asset bundle for an API that is not in it.
    // On the web this is the empty string and the URL is byte-for-byte what it always was.
    response = await fetch(`${apiOrigin()}/api/auth/${path}`,
      { ...init, headers, cache: 'no-store' });
  } catch {
    throw new AuthError('network', 0,
      'Could not reach the server. Try again when you have a connection.');
  }
  if (response.status === 204) return { status: 204, body: null };
  const text = await response.text();
  if (!text) return { status: response.status, body: null };
  try {
    const parsed: unknown = JSON.parse(text);
    return { status: response.status, body: isRecord(parsed) ? parsed : null };
  } catch {
    throw new AuthError('invalid_json', response.status,
      'The server sent a response this app could not read.');
  }
}

async function requestJson(
  path: string,
  init: RequestInit,
  ok: number | readonly number[],
): Promise<JsonObject> {
  const allowed = new Set(Array.isArray(ok) ? ok : [ok]);
  const { status, body } = await authRequest(path, init);
  if (!allowed.has(status)) throw errorFromResponse(status, body);
  if (status === 204) return {};
  if (!body) {
    throw new AuthError('invalid_json', status,
      'The server sent a response this app could not read.');
  }
  return body;
}

export async function createAccount(): Promise<{
  session: AccountSession;
  recoveryCode: string;
}> {
  const body = await requestJson('account', { method: 'POST' }, 201);
  if (typeof body.recoveryCode !== 'string' || body.recoveryCode.length === 0) {
    throw new AuthError('invalid_json', 201,
      'The server sent a response this app could not read.');
  }
  const session = sessionFrom(body);
  storeSession(session);
  setPendingRecovery({ code: body.recoveryCode, rotated: false });
  scheduleSync();
  return { session, recoveryCode: body.recoveryCode };
}

export async function recoverAccount(recoveryCode: string): Promise<AccountSession> {
  const body = await requestJson('recover', {
    method: 'POST',
    body: JSON.stringify({ recoveryCode }),
  }, 201);
  const session = sessionFrom(body);
  storeSession(session);
  setPendingRecovery(null);
  scheduleSync();
  return session;
}

export async function redeemPairing(pairingCode: string): Promise<AccountSession> {
  const body = await requestJson('pair/redeem', {
    method: 'POST',
    body: JSON.stringify({ pairingCode }),
  }, 201);
  const session = sessionFrom(body);
  storeSession(session);
  setPendingRecovery(null);
  scheduleSync();
  return session;
}

export async function rotateRecovery(): Promise<{ recoveryCode: string }> {
  const body = await requestJson('recovery/rotate', { method: 'POST' }, 200);
  if (typeof body.recoveryCode !== 'string' || body.recoveryCode.length === 0) {
    throw new AuthError('invalid_json', 200,
      'The server sent a response this app could not read.');
  }
  setPendingRecovery({ code: body.recoveryCode, rotated: true });
  return { recoveryCode: body.recoveryCode };
}

export async function issuePairing(): Promise<{ pairingCode: string; expiresAt: number }> {
  const body = await requestJson('pair', { method: 'POST' }, 201);
  if (typeof body.pairingCode !== 'string' || typeof body.expiresAt !== 'number') {
    throw new AuthError('invalid_json', 201,
      'The server sent a response this app could not read.');
  }
  return { pairingCode: body.pairingCode, expiresAt: body.expiresAt };
}

export async function signOutThisDevice(): Promise<void> {
  try {
    await requestJson('sign-out', { method: 'POST' }, 204);
  } catch {
    /* Revoke is best-effort; a sign-out tap must still drop the local bearer. */
  }
  storeSession(null);
  setPendingRecovery(null);
}

export async function deleteAccount(): Promise<void> {
  await requestJson('account', { method: 'DELETE' }, 204);
  storeSession(null);
  setPendingRecovery(null);
}

export async function renewSessionIfDue(): Promise<void> {
  const session = accountSession.value;
  if (!session) return;
  if (session.expiresAt - Date.now() > SESSION_RENEW_WITHIN_MS) return;
  const body = await requestJson('session/renew', { method: 'POST' }, 200);
  if (body.renewed === true && typeof body.sessionToken === 'string') {
    const next = parseSession({
      sessionToken: body.sessionToken,
      expiresAt: body.expiresAt,
      user: session.user,
    });
    if (next) storeSession(next);
  }
}

export async function postHistorySync(payload: {
  history: unknown[];
  historyClearedBefore?: number;
}): Promise<{ status: number; body: JsonObject | null }> {
  const body: JsonObject = { history: payload.history };
  if (payload.historyClearedBefore !== undefined) {
    body.historyClearedBefore = payload.historyClearedBefore;
  }
  return authRequest('sync', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
